import { useState, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import Button from './Button';

interface UploadedFile {
  id?: string;
  name: string;
  size: number;
  type: string;
  url?: string;
  file?: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress?: number;
  error?: string;
}

interface FileUploadProps {
  onUpload: (files: File[]) => Promise<string[]>;
  onRemove?: (file: UploadedFile) => void;
  accept?: string;
  multiple?: boolean;
  maxSize?: number; // bytes
  maxFiles?: number;
  existingFiles?: Array<{ id: string; name: string; url: string; size?: number }>;
  disabled?: boolean;
  className?: string;
}

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) {
    return (
      <svg className="w-8 h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
  }
  if (type === 'application/pdf') {
    return (
      <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  }
  if (type.includes('spreadsheet') || type.includes('excel') || type.endsWith('.xlsx') || type.endsWith('.xls')) {
    return (
      <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
      </svg>
    );
  }
  return (
    <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
};

export default function FileUpload({
  onUpload,
  onRemove,
  accept = '*/*',
  multiple = true,
  maxSize = 10 * 1024 * 1024, // 10MB default
  maxFiles = 10,
  existingFiles = [],
  disabled = false,
  className = '',
}: FileUploadProps) {
  // existingFilesがnull/undefined/配列でない場合は空配列として扱う
  const safeExistingFiles = Array.isArray(existingFiles) ? existingFiles : [];
  const [files, setFiles] = useState<UploadedFile[]>(
    safeExistingFiles.map(f => ({
      id: f.id,
      name: f.name,
      size: f.size || 0,
      type: '',
      url: f.url,
      status: 'success' as const,
    }))
  );
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(async (selectedFiles: FileList | null) => {
    if (!selectedFiles || disabled) return;

    const newFiles: UploadedFile[] = [];
    const filesToUpload: File[] = [];
    const errors: string[] = [];

    // ファイル数チェック
    const currentCount = files.filter(f => f.status === 'success').length;
    let addedCount = 0;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];

      // ファイル数制限
      if (currentCount + addedCount >= maxFiles) {
        errors.push(`ファイル数が上限（${maxFiles}件）を超えています`);
        break;
      }

      // サイズチェック
      if (file.size > maxSize) {
        errors.push(`${file.name}: ファイルサイズが上限（${formatFileSize(maxSize)}）を超えています`);
        continue;
      }

      // 重複チェック
      if (files.some(f => f.name === file.name && f.size === file.size)) {
        errors.push(`${file.name}: 同じファイルが既に存在します`);
        continue;
      }

      newFiles.push({
        name: file.name,
        size: file.size,
        type: file.type,
        file,
        status: 'pending',
      });
      filesToUpload.push(file);
      addedCount++;
    }

    if (errors.length > 0) {
      toast.error(errors.join(', '));
    }

    if (filesToUpload.length === 0) return;

    // ペンディング状態で追加
    setFiles(prev => [...prev, ...newFiles]);

    // アップロード開始
    setIsUploading(true);
    setFiles(prev => prev.map(f => 
      filesToUpload.some(uf => uf.name === f.name && uf.size === f.size)
        ? { ...f, status: 'uploading' as const, progress: 0 }
        : f
    ));

    try {
      const urls = await onUpload(filesToUpload);
      
      // 成功状態に更新
      setFiles(prev => prev.map((f, i) => {
        const uploadIndex = filesToUpload.findIndex(uf => uf.name === f.name && uf.size === f.size);
        if (uploadIndex !== -1 && urls[uploadIndex]) {
          return { ...f, status: 'success' as const, url: urls[uploadIndex], progress: 100 };
        }
        return f;
      }));
    } catch (error) {
      console.error('Upload failed:', error);
      // エラー状態に更新
      setFiles(prev => prev.map(f => 
        filesToUpload.some(uf => uf.name === f.name && uf.size === f.size)
          ? { ...f, status: 'error' as const, error: 'アップロードに失敗しました' }
          : f
      ));
    } finally {
      setIsUploading(false);
    }
  }, [files, maxFiles, maxSize, onUpload, disabled]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleRemoveFile = (file: UploadedFile, index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    if (onRemove) {
      onRemove(file);
    }
  };

  const handleRetry = (file: UploadedFile) => {
    if (file.file) {
      handleFiles(new DataTransfer().files); // Reset and re-upload
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* ドロップゾーン */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
          ${isDragging
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-800'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => handleFiles(e.target.files)}
          className="hidden"
          disabled={disabled}
        />

        <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>

        <p className="text-gray-700 dark:text-gray-300 mb-1">
          {isDragging ? 'ここにドロップ' : 'ファイルをドラッグ＆ドロップ'}
        </p>
        <p className="text-sm text-gray-500">
          または <span className="text-blue-600">クリックして選択</span>
        </p>
        <p className="text-xs text-gray-400 mt-2">
          最大 {formatFileSize(maxSize)} / ファイル・最大 {maxFiles} ファイル
        </p>
      </div>

      {/* ファイル一覧 */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className={`flex items-center gap-3 p-3 rounded-lg border ${
                file.status === 'error'
                  ? 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-900/20'
                  : file.status === 'uploading'
                  ? 'border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20'
                  : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
              }`}
            >
              {/* アイコン */}
              <div className="flex-shrink-0">
                {getFileIcon(file.type)}
              </div>

              {/* ファイル情報 */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {file.name}
                  </p>
                  {file.status === 'uploading' && (
                    <div className="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
                  )}
                  {file.status === 'success' && (
                    <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                  {file.status === 'error' && (
                    <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>{formatFileSize(file.size)}</span>
                  {file.status === 'uploading' && file.progress !== undefined && (
                    <span>{file.progress}%</span>
                  )}
                  {file.error && (
                    <span className="text-red-500">{file.error}</span>
                  )}
                </div>

                {/* プログレスバー */}
                {file.status === 'uploading' && (
                  <div className="w-full h-1 bg-gray-200 dark:bg-gray-700 rounded-full mt-1 overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-300"
                      style={{ width: `${file.progress || 0}%` }}
                    />
                  </div>
                )}
              </div>

              {/* アクション */}
              <div className="flex items-center gap-1">
                {file.url && file.status === 'success' && (
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 text-blue-600 hover:text-blue-800"
                    title="プレビュー"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </a>
                )}
                {file.status === 'error' && file.file && (
                  <button
                    onClick={() => handleRetry(file)}
                    className="p-1 text-blue-600 hover:text-blue-800"
                    title="再試行"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                )}
                {!disabled && file.status !== 'uploading' && (
                  <button
                    onClick={() => handleRemoveFile(file, index)}
                    className="p-1 text-red-500 hover:text-red-700"
                    title="削除"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* アップロード中の表示 */}
      {isUploading && (
        <div className="flex items-center gap-2 text-sm text-blue-600">
          <div className="w-4 h-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <span>アップロード中...</span>
        </div>
      )}
    </div>
  );
}
